import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="bd-libraire-fullscreen"
//
// Blows one preview stage up to an overlay sized like the real page. The
// admin column is a ~600px grid track, so the customer-facing partials render
// their tablet layout there (.bd-libraire is max-width 920px with breakpoints
// at 768px and 640px) — the admin was judging copy length and the photo/text
// balance against a width no customer ever sees.
//
// Ephemeral open/closed state only (see rails-hotwire's
// arch-stimulus-boundaries). No HTML is built here: the overlay IS the
// existing stage element, restyled by a class, so the Turbo Frame keeps
// live-refreshing while fullscreen — bd_libraire_form_controller finds the
// frame by id and does not care how it is displayed.
//
// The state lives on <body>, NOT on this element: the form controller reloads
// the whole frame on every keystroke, which destroys and rebuilds these
// controllers. Keeping "which stage is open" outside the frame lets the new
// instance restore it on connect, so editing a field does not collapse the
// overlay the admin is reading.
export default class extends Controller {
  static targets = ["stage"]
  static classes = ["open"]
  static values = { key: String }

  static OPEN_ATTRIBUTE = "data-bd-libraire-fullscreen-open"

  initialize() {
    this.closeOnEscape = this.closeOnEscape.bind(this)
  }

  connect() {
    if (document.body.getAttribute(this.constructor.OPEN_ATTRIBUTE) === this.keyValue) {
      this.show()
    }
  }

  disconnect() {
    // Tear down the listener, but leave the body attribute alone: a frame
    // re-render disconnects us and the next instance reads it to reopen.
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  open() {
    // Only one stage may be open. The overlay covers the viewport, so the
    // other trigger cannot be clicked — but it still takes focus, and Enter
    // on a focused button activates it. Closing any other open stage first
    // keeps "one overlay at a time" true by construction rather than by the
    // overlay happening to sit on top.
    this.closeOthers()

    document.body.setAttribute(this.constructor.OPEN_ATTRIBUTE, this.keyValue)
    this.show()
  }

  close() {
    document.removeEventListener("keydown", this.closeOnEscape)

    if (this.hasStageTarget) this.stageTarget.classList.remove(this.openClass)

    // Only tear down the shared state if this stage is the one holding it:
    // clearing it on behalf of another open stage would unlock scrolling
    // while that overlay is still covering the screen.
    if (document.body.getAttribute(this.constructor.OPEN_ATTRIBUTE) !== this.keyValue) return

    document.body.removeAttribute(this.constructor.OPEN_ATTRIBUTE)
    document.body.classList.remove("bd-libraire-pv-locked")
  }

  closeOthers() {
    const openClass = this.openClass

    document.querySelectorAll("." + openClass).forEach((stage) => {
      if (stage === this.stageTarget) return

      stage.classList.remove(openClass)
    })
  }

  // Clicking the backdrop closes; clicking the preview itself must not.
  closeOnBackdrop(event) {
    if (event.target === this.stageTarget) this.close()
  }

  closeOnEscape(event) {
    if (event.key === "Escape") this.close()
  }

  show() {
    if (!this.hasStageTarget) return

    this.stageTarget.classList.add(this.openClass)
    document.body.classList.add("bd-libraire-pv-locked")
    document.addEventListener("keydown", this.closeOnEscape)
  }
}
